import { AbstractModel } from '../base';
import { IInvoice, Invoice } from '../invoice';
import { IQuote, Quote } from '../quote';
import {
  PaymentPlanAmountTypeEnum,
  PaymentPlanScheduleDateTypeEnum,
  PaymentPlanScheduleTypeEnum,
  PaymentPlanStateEnum
} from './payment-plan.constants';
import {
  IPaymentPlan,
  IPaymentPlanData,
  IPaymentPlanSchedule,
  IPaymentPlanScheduleData
} from './payment-plan.types';

export class PaymentPlan extends AbstractModel implements IPaymentPlan {
  readonly id?: number;
  readonly ex_factory_date: string | null = null;
  readonly sign_off_date: string | null = null;
  readonly customer_name: string;
  readonly customer_number: string = '';
  readonly csi_order_number: string = '';
  readonly po_number: string = '';
  readonly quote: number | IQuote;
  readonly state: PaymentPlanStateEnum;
  readonly total_amount_paid: number = 0;
  readonly total_amount_scheduled: number = 0;
  readonly total_tax_paid: number = 0;
  readonly schedules: IPaymentPlanSchedule[] = [];
  readonly next_payment_date: string | null = null;
  readonly created?: Date;
  readonly modified?: Date;
  constructor(data: IPaymentPlanData) {
    super();

    const requiredFields: (keyof IPaymentPlanData)[] = [
      'quote',
      'customer_name'
    ];

    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    this.customer_name = data.customer_name!;
    if (data.customer_number) {
      this.customer_number = data.customer_number;
    }
    if (data.csi_order_number) {
      this.csi_order_number = data.csi_order_number;
    }
    if (data.po_number) {
      this.po_number = data.po_number;
    }
    if (data.ex_factory_date) {
      this.ex_factory_date = data.ex_factory_date;
    }
    if (data.sign_off_date) {
      this.sign_off_date = data.sign_off_date;
    }
    this.quote =
      typeof data.quote == 'number' ? data.quote! : new Quote(data.quote!);

    this.state =
      typeof data.state == 'number' ? data.state : PaymentPlanStateEnum.Active;

    if (data.total_amount_paid) {
      this.total_amount_paid = data.total_amount_paid;
    }
    if (data.total_amount_scheduled) {
      this.total_amount_scheduled = data.total_amount_scheduled;
    }
    if (data.total_tax_paid) {
      this.total_tax_paid = data.total_tax_paid;
    }
    if (data.schedules) {
      this.schedules = data.schedules!.map(
        schedule => new PaymentPlanSchedule(schedule)
      );
    }

    if (data.next_payment_date) {
      this.next_payment_date = data.next_payment_date;
    }

    if (data.created) {
      this.created =
        typeof data.created == 'string' ? new Date(data.created) : data.created;
    }

    if (data.modified) {
      this.modified =
        typeof data.modified == 'string'
          ? new Date(data.modified)
          : data.modified;
    }
  }

  get total_plan_amount(): number {
    return (
      ((this.quote as IQuote).equipment_nettotal ?? 0) +
      ((this.quote as IQuote).services_subtotal ?? 0) +
      ((this.quote as IQuote).extras_subtotal ?? 0) +
      ((this.quote as IQuote).freight_subtotal ?? 0)
    );
  }

  toQuoteCustomerString(): string {
    return `${this.quote} - ${this.customer_name}`;
  }
}

export class PaymentPlanSchedule extends AbstractModel
  implements IPaymentPlanSchedule {
  readonly id?: number;
  readonly payment_type: PaymentPlanScheduleTypeEnum;
  readonly calculated_amount_due: number = 0;
  readonly amount_type: PaymentPlanAmountTypeEnum =
    PaymentPlanAmountTypeEnum.FixedAmount;
  readonly due_date: string | null = null;
  readonly date_type: PaymentPlanScheduleDateTypeEnum =
    PaymentPlanScheduleDateTypeEnum.ExFactory;
  readonly csi_order_number: string = '';
  readonly po_number: string = '';
  readonly calculated_due_date: string | null = null;
  readonly reminder_start_date: string | null = null;
  readonly number_of_weeks: number | null = null;
  readonly fixed_amount: string | null = null;
  readonly percentage: string | null = null;
  readonly invoice: number | IInvoice | null = null;

  constructor(data: IPaymentPlanScheduleData) {
    super();

    const requiredFields: (keyof IPaymentPlanSchedule)[] = [
      'payment_type',
      'date_type'
    ];

    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }

    this.payment_type = data.payment_type!;
    if (data.due_date) {
      this.due_date = data.due_date;
    }

    this.date_type = data.date_type!;
    if (data.csi_order_number) {
      this.csi_order_number = data.csi_order_number;
    }
    if (data.amount_type) {
      this.amount_type = data.amount_type;
    }
    if (data.fixed_amount) {
      this.fixed_amount = data.fixed_amount;
    }
    if (data.percentage) {
      this.percentage = data.percentage;
    }
    if (data.calculated_amount_due) {
      this.calculated_amount_due = data.calculated_amount_due;
    }
    if (data.reminder_start_date) {
      this.reminder_start_date = data.reminder_start_date;
    }
    if (data.number_of_weeks) {
      this.number_of_weeks = data.number_of_weeks;
    }
    if (data.due_date) {
      this.due_date = data.due_date;
    }
    if (data.calculated_due_date) {
      this.calculated_due_date = data.calculated_due_date;
    }
    if (data.po_number) {
      this.po_number = data.po_number;
    }

    if (data.invoice) {
      this.invoice =
        typeof data.invoice == 'number'
          ? data.invoice
          : new Invoice(data.invoice);
    }
  }
}
